
import React, { useEffect } from "react";
import { Button, Toast, ToastContainer } from "react-bootstrap";

/**
 * A toast notification component to display messages of different types.
 * Supports success, danger, and warning styles with corresponding icons.
 * @memberof module:components/modals
 * @param {Object} props - Component props.
 * @param {boolean} props.show - Whether the toast is visible.
 * @param {"success" | "danger" | "warning"} props.type - Type of the toast message, controls style and icon.
 * @param {React.ReactNode} props.message - The message content to display inside the toast.
 * @param {boolean} [props.autohide=false] - Whether the toast should automatically hide after a delay.
 * @param {number} [props.delay=2000] - Duration in milliseconds before the toast auto hides.
 * @param {function(): void} props.onDismiss - Callback fired when the toast is dismissed either manually or automatically.
 * 
 * @returns {JSX.Element} The message toast component.
 */
const MessageToast = ({
  show,
  type,
  message,
  autohide = false,
  delay = 2000,
  onDismiss,
}) => {
  const toastStyles = {
    success: {
      background: "#182318",
      color: "#55aa56",
      border: "1px solid #55aa56",
      icon: "bi bi-check-circle-fill",
    },
    danger: {
      background: "#231818",
      color: "#ff5c5c",
      border: "1px solid #ff5c5c",
      icon: "bi bi-exclamation-circle-fill",
    },
    warning: {
      background: "#232318",
      color: "#ffaa00",
      border: "1px solid #ffaa00",
      icon: "bi bi-exclamation-triangle-fill",
    },
  };

  const style = toastStyles[type] || toastStyles.success;

  return (
    <ToastContainer position="top-center" style={{ width: "100%" }}>
      <Toast
        className="m-4"
        onClose={() => onDismiss()}
        autohide={autohide}
        show={show}
        delay={delay}
        style={{ width: "auto", ...style }}
      >
        <Toast.Body className="d-flex justify-content-between align-items-center m-0 p-2">
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <i class={style.icon} style={{ fontSize: "1.3rem" }}></i>
            <span>{message}</span>
          </div>
          <Button
            variant="link"
            style={{ color: style.color }}
            size="sm"
            onClick={() => {
              onDismiss();
            }}
          >
            <b>Dismiss</b>
          </Button>
        </Toast.Body>
      </Toast>
    </ToastContainer>
  );
};

export default MessageToast;